import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  SimpleGrid,
  useToast
} from "@chakra-ui/react";
import { createPool, updatePool } from "../../../api/partnersApi";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import { useTranslation } from "react-i18next";
import {
  MODAL_CONTENT_SX,
  MODAL_HEADER_SX,
  MODAL_FOOTER_SX,
} from "../../../components/Portal/portalModalChrome";

const EMPTY_FORM = {
  name: "",
  code: "",
  description: ""
};

const PoolFormModal = ({ isOpen, onClose, initialData, onSuccess }) => {
  const { selectedBusinessUnitId } = useBusinessUnit();
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();
  const { t } = useTranslation("portal");

  const isEdit = Boolean(initialData?.id);

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) {
      setForm({
        name: initialData?.name || "",
        code: initialData?.code || "",
        description: initialData?.description || ""
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [initialData, isEdit, isOpen]);

  const isValid = useMemo(() => {
    return form.name.trim().length > 0 && form.code.trim().length > 0;
  }, [form.name, form.code]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "code") {
      setForm((prev) => ({ ...prev, [name]: value.toUpperCase() }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async () => {
    if (!isValid) return;
    setIsSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        code: form.code.trim(),
        description: form.description.trim() || undefined
      };

      if (isEdit) {
        const updated = await updatePool(initialData.id, payload, { buId: selectedBusinessUnitId || undefined });
        toast({ title: t("partners.pools.form.toasts.updated", "Pool updated"), status: "success" });
        onSuccess?.(updated);
      } else {
        const created = await createPool(payload, { buId: selectedBusinessUnitId || undefined });
        toast({ title: t("partners.pools.form.toasts.created", "Pool created"), status: "success" });
        onSuccess?.(created);
      }
      onClose?.();
    } catch (e) {
      toast({
        title: t("partners.pools.form.toasts.operationFailedTitle", "Operation failed"),
        description: e?.response?.data?.message || t("partners.pools.form.toasts.operationFailedDescription", "An error occurred. Please try again."),
        status: "error"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay bg="rgba(11,31,58,.45)" />
      <ModalContent sx={MODAL_CONTENT_SX}>
        <ModalHeader sx={MODAL_HEADER_SX}>
          {isEdit ? t("partners.pools.form.modalTitleEdit", "Edit Pool") : t("partners.pools.form.modalTitleCreate", "Create Pool")}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl isRequired>
              <FormLabel>{t("partners.pools.form.fields.name", "Name")}</FormLabel>
              <Input
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder={t("partners.pools.form.placeholders.name", "Pool name")}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>{t("partners.pools.form.fields.code", "Code")}</FormLabel>
              <Input
                name="code"
                value={form.code}
                onChange={handleChange}
                placeholder={t("partners.pools.form.placeholders.code", "POOL_CODE")}
              />
            </FormControl>
          </SimpleGrid>
          <FormControl mt={4}>
            <FormLabel>{t("partners.pools.form.fields.description", "Description")}</FormLabel>
            <Textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder={t("partners.pools.form.placeholders.description", "Optional description")}
            />
          </FormControl>
        </ModalBody>
        <ModalFooter sx={MODAL_FOOTER_SX}>
          <Button variant="secondary" size="sm" onClick={onClose}>
            {t("partners.pools.form.buttons.cancel", "Cancel")}
          </Button>
          <Button variant="primary" size="sm" onClick={handleSubmit} isDisabled={!isValid} isLoading={isSubmitting}>
            {isEdit ? t("partners.pools.form.buttons.saveChanges", "Save Changes") : t("partners.pools.form.buttons.create", "Create")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PoolFormModal;
